import React, { useState } from 'react';
import { addDoc, collection, serverTimestamp, Timestamp, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import { Box, Typography, Button, CircularProgress, IconButton } from '@mui/material';
import { ContentCopy, Check, PersonAdd, Lock } from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { colors, typography } from '../../theme';

// ─── Plan barber limits ───────────────────────────────────────────────────────
const PLAN_LIMITS = { free: 1, basic: 3, pro: 999, master: 999 };
const PLAN_LABELS = { free: 'Free', basic: 'Basic', pro: 'Pro', master: 'Master' };
const PLAN_NEXT   = { free: 'Basic (RM49/mo)', basic: 'Pro (RM99/mo)', pro: 'Master (RM150/mo)' };

// ─── Component ────────────────────────────────────────────────────────────────
const InviteBarber = ({ shopId, shopName, shop, currentBarberCount }) => {
    const [loading, setLoading] = useState(false);
    const [code,    setCode]    = useState(null);
    const [copied,  setCopied]  = useState(false);
    const [error,   setError]   = useState('');

    const plan        = shop?.plan ?? 'free';
    const limit       = PLAN_LIMITS[plan] ?? 1;
    const count       = currentBarberCount ?? 0;
    const atLimit     = count >= limit;
    const nextPlan    = PLAN_NEXT[plan];

    const handleGenerate = async () => {
        if (atLimit) return;
        setLoading(true);
        setError('');
        try {
            const expiresAt = new Date();
            expiresAt.setDate(expiresAt.getDate() + 7);

            const ref = await addDoc(collection(db, 'invites'), {
                shopId,
                shopName,
                used:      false,
                expiresAt: Timestamp.fromDate(expiresAt),
                createdAt: serverTimestamp(),
            });

            const generatedCode = ref.id.slice(0, 8).toUpperCase();
            await updateDoc(doc(db, 'invites', ref.id), { code: generatedCode });
            setCode(generatedCode);
        } catch (e) {
            console.error(e);
            setError('Failed to generate invite. Try again.');
        }
        setLoading(false);
    };

    const joinUrl = code ? `${window.location.origin}/join?code=${code}` : '';

    const handleCopy = () => {
        navigator.clipboard.writeText(joinUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ bgcolor: colors.bgCard, border: `1px solid ${colors.primary}`, borderRadius: 2, p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PersonAdd sx={{ fontSize: 18, color: colors.primary }} />
                <Typography sx={{ fontFamily: typography.poppins, fontSize: 15, fontWeight: 600, color: colors.textPrimary, flex: 1 }}>
                    Invite a Barber
                </Typography>
                {/* Barber count indicator */}
                <Box sx={{
                    px: 1.2, py: 0.3, borderRadius: 1,
                    bgcolor: atLimit ? colors.errorBg : colors.successBg,
                    border: `1px solid ${atLimit ? colors.errorBorder : colors.successBorder}`,
                }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: atLimit ? colors.error : colors.success }}>
                        {count} / {limit === 999 ? '∞' : limit}
                    </Typography>
                </Box>
            </Box>

            {/* At limit — show upgrade prompt */}
            {atLimit ? (
                <Box sx={{ bgcolor: colors.bg, border: `1px solid ${colors.bg}`, borderRadius: 2, p: 2, textAlign: 'center' }}>
                    <Lock sx={{ fontSize: 28, color: colors.error, mb: 1 }} />
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.error, mb: 0.5 }}>
                        Barber limit reached
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: colors.textFaint, mb: 1.5 }}>
                        Your <Typography component="span" sx={{ color: colors.primary }}>{PLAN_LABELS[plan]}</Typography> plan allows up to{' '}
                        <Typography component="span" sx={{ color: colors.primary }}>{limit} barber{limit !== 1 ? 's' : ''}</Typography>.
                        {nextPlan && ` Upgrade to ${nextPlan} to add more.`}
                    </Typography>
                    <Box sx={{ bgcolor: colors.bgCard, border: '1px solid #c8a97e44', borderRadius: 2, px: 2, py: 1 }}>
                        <Typography sx={{ fontSize: 12, color: colors.primary }}>
                            📱 Contact DigitalQ to upgrade
                        </Typography>
                    </Box>
                </Box>
            ) : (
                <>
                    <Typography sx={{ fontSize: 12, color: colors.textFaint, mb: 2 }}>
                        Generate a one-time invite link. Share it with your barber — valid for 7 days.
                        {limit !== 999 && (
                            <Typography component="span" sx={{ color: colors.textFaintest, fontSize: 11 }}>
                                {' '}({limit - count} slot{limit - count !== 1 ? 's' : ''} remaining)
                            </Typography>
                        )}
                    </Typography>

                    {!code ? (
                        <Button fullWidth variant="contained" onClick={handleGenerate} disabled={loading}
                            sx={{ bgcolor: colors.primary, color: colors.bg, fontWeight: 700, textTransform: 'none', borderRadius: 2, py: 1, '&:hover': { bgcolor: colors.primaryHover }, '&:disabled': { bgcolor: colors.border, color: colors.textFaintest } }}>
                            {loading ? <CircularProgress size={16} sx={{ color: colors.textMuted }} /> : '✉️ Generate Invite Link'}
                        </Button>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'center', bgcolor: colors.white, borderRadius: 2, p: 2 }}>
                                <QRCodeSVG value={joinUrl} size={160} />
                            </Box>
                            <Box sx={{ bgcolor: colors.bg, border: '1px solid #3e2c1c', borderRadius: 2, p: 1.5, textAlign: 'center' }}>
                                <Typography sx={{ fontSize: 11, color: colors.textFaint, mb: 0.5 }}>Invite Code</Typography>
                                <Typography sx={{ fontFamily: 'monospace', fontSize: 24, fontWeight: 700, color: colors.primary, letterSpacing: 6 }}>
                                    {code}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: colors.bg, border: '1px solid #3e2c1c', borderRadius: 2, p: 1.2, pr: 0.5 }}>
                                <Typography sx={{ fontSize: 11, color: colors.textFaintest, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                                    {joinUrl}
                                </Typography>
                                <IconButton size="small" onClick={handleCopy} sx={{ color: copied ? colors.success : colors.primary, flexShrink: 0 }}>
                                    {copied ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
                                </IconButton>
                            </Box>
                            <Typography sx={{ fontSize: 11, color: colors.textFaintest, textAlign: 'center' }}>
                                ⏳ Expires in 7 days · One-time use only
                            </Typography>
                            <Button size="small" onClick={() => { setCode(null); setError(''); }}
                                sx={{ color: colors.textFaint, textTransform: 'none', fontSize: 12, '&:hover': { color: colors.textMuted } }}>
                                Generate new code
                            </Button>
                        </Box>
                    )}
                </>
            )}

            {error && (
                <Typography sx={{ fontSize: 12, color: colors.error, mt: 1, textAlign: 'center' }}>{error}</Typography>
            )}
        </Box>
    );
};

export default InviteBarber;